import React, { Suspense } from "react";
import { Subject, Subscription } from "rxjs";
import { ICartProps } from ".";
import ConfigProps from "../../constants/configProps";
import { IAvailabilityInfo } from "../../interfaces/IAvailabilityInfo";
import { ICartTotals } from "../../interfaces/ICartTotals";
import { IShoppingLocation } from "../../interfaces/IShoppingLocation";
import { IWorkflow } from "../../interfaces/IWorkflow";
import ConfigPropService from "../../utilities/configPropService";
import { ProductAvailabilityService } from "../../utilities/productAvailabilityService";
import ServerSideService from "../../utilities/serverSideService";
import UserService from "../../utilities/userService";
import Icon from "../icon/icon";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { SuccessAlert } from "wbm-generic-components";

const Cart = React.lazy(
  () =>
    import(
      /* webpackMode: "eager" */
      /* webpackChunkName: "slide-outs" */
      "."
    )
);

export interface ICartWrapperProps extends ICartProps {
  productAvailabilityCallback: (availabilityInfo: IAvailabilityInfo[]) => void;
  shoppingLocation?: IShoppingLocation;
  cartTotals: ICartTotals;
  safeZoneHeight: number;
  workflow: IWorkflow;
  custAccount: string;
}

export interface ICartWrapperState {
  productAvailabilityAPIName: string;
  displayAvailabilityServiceMessage: boolean;
  shoppingLocation?: IShoppingLocation;
  cartTotals: ICartTotals | undefined;
  displayAvailability: boolean;
}

class CartWrapper extends React.Component<
  ICartWrapperProps,
  ICartWrapperState
> {
  static productAvailabilitySubject = new Subject<{
    props: ICartWrapperProps;
    state: ICartWrapperState;
  }>();

  constructor(props: ICartWrapperProps) {
    super(props);

    this.state = {
      productAvailabilityAPIName: "",
      displayAvailabilityServiceMessage: false,
      shoppingLocation: undefined,
      cartTotals: undefined,
      displayAvailability: false,
    };
  }

  private configPropSub: Subscription | undefined;
  private productAvailabilityRequest: Subscription | undefined;
  private availabilityInfo: Subscription | undefined;

  componentDidMount() {
    this.configPropSub = ConfigPropService.get([
      ConfigProps.ProductAvailabilityAPIName,
      ConfigProps.DisplayAvailabilityServiceMessage,
      ConfigProps.DisplayAvailability,
    ]).subscribe((configProps) => {
      this.setState({
        productAvailabilityAPIName:
          configProps[ConfigProps.ProductAvailabilityAPIName],
        displayAvailabilityServiceMessage: ConfigPropService.isYesOrTrue(
          configProps[ConfigProps.DisplayAvailabilityServiceMessage]
        ),
        displayAvailability: ConfigPropService.isYesOrTrue(
          configProps[ConfigProps.DisplayAvailability]
        ),
      });
    });

    this.productAvailabilityRequest = CartWrapper.productAvailabilitySubject
      .asObservable()
      .subscribe(
        (propsAndState: {
          props: ICartWrapperProps;
          state: ICartWrapperState;
        }) => {
          this.props.productAvailabilityCallback([]);

          const dlvMode =
            propsAndState.props.shoppingLocation?.dlvMode ||
            propsAndState.props.shoppingLocation?.dlvModeId;
          const apiName = propsAndState.state.productAvailabilityAPIName;
          const altAddressRecId = propsAndState.props.shoppingLocation
            ? propsAndState.props.shoppingLocation.mbsAltAddressRecId ||
              propsAndState.props.shoppingLocation.displayId
            : "";

          const state = propsAndState.props.shoppingLocation?.state;
          const zipCode = propsAndState.props.shoppingLocation?.zipCode;

          if (this.availabilityInfo) {
            this.availabilityInfo.unsubscribe();
          }

          const items = propsAndState.props.cartTotals.items.map((item) => {
            return {
              unitId: item.itemIdentifier.unitId,
              itemId: item.itemIdentifier.id,
            };
          });

          if (dlvMode) {
            this.availabilityInfo = ProductAvailabilityService.get(
              dlvMode,
              apiName,
              altAddressRecId,
              items,
              this.props.custAccount,
              state,
              zipCode
            ).subscribe((response) => {
              this.props.productAvailabilityCallback(response);
            });
          }
        }
      );
  }

  componentWillUnmount() {
    if (this.configPropSub) {
      this.configPropSub.unsubscribe();
    }

    if (this.productAvailabilityRequest) {
      this.productAvailabilityRequest.unsubscribe();
    }

    if (this.availabilityInfo) {
      this.availabilityInfo.unsubscribe();
    }
  }

  static getDerivedStateFromProps(
    props: ICartWrapperProps,
    state: ICartWrapperState
  ) {
    if (
      props.cartTotals &&
      props.cartTotals.items &&
      props.shoppingLocation &&
      state.productAvailabilityAPIName &&
      state.displayAvailabilityServiceMessage &&
      state.displayAvailability &&
      UserService.displayUserInfo() &&
      (props.shoppingLocation.dlvMode || props.shoppingLocation.dlvModeId) &&
      (props.cartTotals !== state.cartTotals ||
        props.shoppingLocation !== state.shoppingLocation)
    ) {
      CartWrapper.productAvailabilitySubject.next({ props, state });

      return {
        cartTotals: props.cartTotals,
        shoppingLocation: props.shoppingLocation,
      };
    }

    return null;
  }

  render() {
    const cartIcon = (
      <>
        <div style={{ height: this.props.safeZoneHeight }}></div>
        <Icon
          className={
            "cart-slide-in suspense-cart" +
            (this.props.workflow &&
            this.props.workflow.isWorkflow &&
            UserService.displayUserInfo()
              ? " workflow-cart"
              : "")
          }
        >
          shopping_cart
        </Icon>
      </>
    );

    const ErrorFallbackRender = (fallbackProps: FallbackProps): JSX.Element => {
      return (
        <SuccessAlert
          timerLengthInMS={3500}
          danger
          dangerText="Please try reloading the page. If the problem persists, please call 1-888-WB-MASON."
          dangerAttentionText="An error has occurred!"
          onClickClose={fallbackProps.resetErrorBoundary}
        />
      );
    };

    return (
      <>
        {ServerSideService.isServerSide || !UserService.displayUserInfo() ? (
          cartIcon
        ) : (
          <ErrorBoundary fallbackRender={ErrorFallbackRender}>
            <Suspense fallback={cartIcon}>
              <Cart {...this.props} />
            </Suspense>
          </ErrorBoundary>
        )}
      </>
    );
  }
}

export default CartWrapper;
